/**
 * Analytics Constants
 * Configuration and constants for analytics tracking
 */

import { getViteMetaEnv } from '@/utils/viteMetaEnv';

const viteEnv = getViteMetaEnv();

// PostHog configuration
export const POSTHOG_CONFIG = {
  // API key from environment variable
  apiKey: viteEnv.VITE_POSTHOG_API_KEY || "",
  // Host URL (can be overridden for EU region)
  host: viteEnv.VITE_POSTHOG_HOST || "https://eu.i.posthog.com",
  // Library identification
  lib: "colorscales-website",
  libVersion: "1.0.0",
} as const;

// LocalStorage keys
export const ANALYTICS_STORAGE_KEYS = {
  DISTINCT_ID: "colorscales_posthog_distinct_id",
  TRACKING_ENABLED: "colorscales_analytics_enabled",
  UTM_PARAMS: "colorscales_utm",
  FIRST_EMAIL_CAPTURED: "colorscales_first_email_captured",
  INTERNAL_USER: "colorscales_internal_user", // Team member opt-out
  disabled: "colorscales_analytics_disabled", // Opt-out key (legacy)
} as const;

// Default event properties (added to all events)
export const DEFAULT_EVENT_PROPERTIES = {
  product: "colorscales-website",
  $lib: POSTHOG_CONFIG.lib,
  $lib_version: POSTHOG_CONFIG.libVersion,
  // Environment tracking for dev/prod filtering
  environment: viteEnv.MODE || "production",
  is_development: viteEnv.DEV || false,
  // Launch phase for filtering events by phase
  launch_phase: viteEnv.VITE_LAUNCH_MODE || "stable",
} as const;

// Analytics feature flags
export const ANALYTICS_FLAGS = {
  // Enable/disable analytics globally (can be overridden by localStorage)
  enabled: true, // Analytics enabled
  // Log analytics calls in development
  debug: viteEnv.DEV || false,
  // Skip analytics in tests
  skipInTests: viteEnv.MODE === "test",
} as const;

/**
 * Runtime Analytics Control
 * Helpers to enable/disable analytics at runtime (useful for debugging)
 */
export const AnalyticsControl = {
  /**
   * Disable analytics (persisted in localStorage)
   */
  disable(): void {
    try {
      if (typeof window !== "undefined" && window.localStorage) {
        window.localStorage.setItem(
          ANALYTICS_STORAGE_KEYS.TRACKING_ENABLED,
          "false",
        );
        console.log(
          "✅ Analytics disabled. Reload the page for it to take effect.",
        );
      }
    } catch (error) {
      console.warn(
        "⚠️ Unable to disable analytics: localStorage not available",
      );
    }
  },

  /**
   * Enable analytics (persisted in localStorage)
   */
  enable(): void {
    try {
      if (typeof window !== "undefined" && window.localStorage) {
        window.localStorage.setItem(
          ANALYTICS_STORAGE_KEYS.TRACKING_ENABLED,
          "true",
        );
        console.log("✅ Analytics enabled. Reload the page for it to take effect.");
      }
    } catch (error) {
      console.warn("⚠️ Unable to enable analytics: localStorage not available");
    }
  },

  /**
   * Mark yourself as internal team member (excluded from analytics)
   * Use this on production to test without polluting analytics
   */
  markAsInternal(): void {
    try {
      if (typeof window !== "undefined" && window.localStorage) {
        window.localStorage.setItem(ANALYTICS_STORAGE_KEYS.INTERNAL_USER, "true");
        console.log("✅ Marked as internal user. You will be excluded from analytics.");
        console.log("   Reload the page for it to take effect.");
      }
    } catch (error) {
      console.warn("⚠️ Unable to mark as internal: localStorage not available");
    }
  },

  /**
   * Remove internal user flag
   */
  unmarkAsInternal(): void {
    try {
      if (typeof window !== "undefined" && window.localStorage) {
        window.localStorage.removeItem(ANALYTICS_STORAGE_KEYS.INTERNAL_USER);
        console.log("✅ Internal user flag removed. Analytics will track you.");
        console.log("   Reload the page for it to take effect.");
      }
    } catch (error) {
      console.warn("⚠️ Unable to unmark as internal: localStorage not available");
    }
  },

  /**
   * Check current analytics status
   */
  status(): void {
    try {
      if (typeof window !== "undefined" && window.localStorage) {
        const stored = window.localStorage.getItem(
          ANALYTICS_STORAGE_KEYS.TRACKING_ENABLED,
        );
        const isInternal = window.localStorage.getItem(
          ANALYTICS_STORAGE_KEYS.INTERNAL_USER,
        ) === "true";
        const isDev = getViteMetaEnv().DEV;
        
        const enabled = stored !== "false" && !isInternal && !isDev;
        
        console.log(`📊 Analytics status: ${enabled ? "ENABLED" : "DISABLED"}`);
        console.log(`   Environment: ${isDev ? "Development" : "Production"}`);
        console.log(`   Internal user: ${isInternal ? "Yes (excluded)" : "No"}`);
        console.log(`\n   Commands:`);
        console.log(`   - AnalyticsControl.markAsInternal() - Exclude yourself from tracking`);
        console.log(`   - AnalyticsControl.unmarkAsInternal() - Include yourself in tracking`);
        console.log(`   - AnalyticsControl.disable() - Disable analytics entirely`);
        console.log(`   - AnalyticsControl.enable() - Enable analytics`);
      }
    } catch (error) {
      console.warn(
        "⚠️ Unable to check analytics status: localStorage not available",
      );
    }
  },
};

